window.customElements.define(
  'custom-variant-picker-toggle',
  class CustomVariantPickerToggle extends window.HTMLElement {
    connectedCallback() {
      this.handleBuyOnceSubmit = this.handleBuyOnceSubmit.bind(this);
      this.initDOMElements();
      this.initEventListeners();

      const currentVariant = this.form?.querySelector('input[type="radio"]:checked');
      if (currentVariant) {
        this.handleUpdateUI(this.getCurrentVariantData());
      }
    }

    disconnectedCallback() {
      this.removeEventListeners();
    }

    // Initialize DOM elements
    initDOMElements() {
      this.form = this.querySelector('form');
      this.formContainer = this.querySelector('.custom-variant-picker-toggle__form-container');
      this.addToCartPrice = this.querySelector('#add-to-cart-price');
      this.addToCartCompareAtPrice = this.querySelector('#add-to-cart-compare-at-price');
      this.errorMessage = this.querySelector('#error-message');
      this.addToCartButton = this.querySelector('#add-to-cart');
      this.notifyMeButton = this.querySelector('#notify-me-button');
      this.oneTimeSwatch = this.querySelector('#swatch-item-one-time-purchase');
      this.subscribeSwatch = this.querySelector('#swatch-item-subscribe-and-save');
      this.variants = this.form.querySelectorAll('.custom-variant-input');
      this.savings = this.querySelectorAll('#savings');
      this.savingsWrapper = this.querySelectorAll('#savings-wrapper');
      this.optionUnitPriceWrapper = this.querySelectorAll('#option-unit-price-wrapper');
      this.optionUnitPrices = this.querySelectorAll('#option-unit-price');
      this.pauseOrCancelAnytimeText = this.querySelector('#pause-or-cancel-anytime');
      this.currentDeliveryInterval = this.querySelector('#current-delivery-interval');
      this.buyOnceForm = this.querySelector('#buy-once-form');
      this.buyOncePrice = this.querySelector('#buy-once-price');
    }

    // Event Listeners
    initEventListeners() {
      this.form.addEventListener('change', this.handleChange.bind(this));
      this.form.addEventListener('submit', this.handleAddToCartSubmit.bind(this));
      this.oneTimeSwatch.addEventListener('click', this.handleChange.bind(this));
      this.subscribeSwatch.addEventListener('click', this.handleChange.bind(this));
      this.buyOnceForm?.addEventListener('submit', this.handleBuyOnceSubmit);

      // Listen for sticky add to cart changes
      window.addEventListener('custom-sticky-add-to-cart:change', this.handleStickyAddToCartChange.bind(this));
    }

    removeEventListeners() {
      this.form.removeEventListener('change', this.handleChange.bind(this));
      this.form.removeEventListener('submit', this.handleAddToCartSubmit.bind(this));
      this.oneTimeSwatch.removeEventListener('click', this.handleChange.bind(this));
      this.subscribeSwatch.removeEventListener('click', this.handleChange.bind(this));
      this.buyOnceForm?.removeEventListener('submit', this.handleBuyOnceSubmit);

      // Remove sticky add to cart listener
      window.removeEventListener('custom-sticky-add-to-cart:change', this.handleStickyAddToCartChange.bind(this));
    }

    // Event Handlers
    handleStickyAddToCartChange(event) {
      const { variant } = event.detail;

      if (!variant.variantId) return;
      const variantInput = this.form.querySelector(`input[type="radio"][value="${variant.variantId}"]`);

      if (!variantInput) return;
      variantInput.checked = true;

      const currentVariantData = this.getCurrentVariantData();
      this.handleUpdateUI(currentVariantData);
      this.handleUpdateUrl(currentVariantData);
    }

    handleChange(event) {
      const currentVariantData = this.getCurrentVariantData();

      this.handleUpdateUI(currentVariantData, event.target);
      this.handleUpdateUrl(currentVariantData);

      this.dispatchEvent(
        new CustomEvent('custom-variant-picker:change', {
          detail: {
            variant: {
              available: currentVariantData.available,
              variantId: currentVariantData.variantId,
              sellingPlanId: currentVariantData.sellingPlanId,
              price: currentVariantData.price,
              compareAtPrice: currentVariantData.crossedPrice,
              subscriptionPrice: currentVariantData.subscriptionPrice,
            },
            element: event.target,
          },
          bubbles: true,
        })
      );
    }

    async handleAddToCartSubmit(event) {
      event.preventDefault();
      if (this.isSubmitting) return;
      this.isSubmitting = true;
      const currentVariantData = this.getCurrentVariantData();
      const cartItem = this.buildCartItem(currentVariantData);

      try {
        this.setLoadingState(true);
        await this.addToCart(cartItem);
        this.dispatchCartRefresh();
        this.closeModalIfNeeded();
      } catch (error) {
        this.handleAddToCartError(error);
      } finally {
        this.setLoadingState(false);
        this.isSubmitting = false;
      }
    }

    buildCartItem(currentVariantData) {
      const sellingPlan = this.determineSellingPlan(currentVariantData);

      return {
        id: currentVariantData.variantId,
        quantity: 1,
        selling_plan: sellingPlan,
      };
    }

    determineSellingPlan(currentVariantData) {
      const activePaymentOption = this.getActivePaymentOption();
      return activePaymentOption === 'one-time' ? '' : currentVariantData.sellingPlanId;
    }

    setLoadingState(isLoading) {
      this.form.classList.toggle('is-loading', isLoading);
    }

    handleAddToCartError(error) {
      console.error('Failed to add subscription to cart:', error);
      this.showErrorMessage(error.message);
    }

    showErrorMessage(message) {
      if (this.errorMessage) {
        this.errorMessage.textContent = message;
      }
      this.errorMessage && this.errorMessage.classList.remove('hidden');

      setTimeout(() => {
        this.errorMessage && this.errorMessage.classList.add('hidden');
      }, 3000);
    }

    async handleBuyOnceSubmit(event) {
      event.preventDefault();
      if (this.isSubmitting) return;
      this.isSubmitting = true;
      const currentVariantData = this.getCurrentVariantData();

      try {
        this.buyOnceForm.classList.add('is-loading');
        await this.addToCart({
          id: currentVariantData.variantId,
          quantity: 1,
        });

        this.dispatchCartRefresh();
        this.closeModalIfNeeded();
      } catch (error) {
        console.error('Failed to add item to cart:', error);
        this.handleAddToCartError(error);
      } finally {
        this.buyOnceForm.classList.remove('is-loading');
        this.isSubmitting = false;
      }
    }

    // UI Update Methods
    handleUpdateUI(currentVariantData, eventTarget = null) {
      this.updateButtonVisibility(currentVariantData);
      this.updateDeliveryInterval(currentVariantData);

      if (eventTarget) {
        this.handleSwatchClick(eventTarget);
      }

      this.updatePriceDisplay();

      if (this.buyOncePrice) {
        const activeVariantInput = this.getActiveVariantInput();
        this.buyOncePrice.textContent = activeVariantInput?.dataset.variantPrice || '';
      }

      if (this.buyOnceForm) {
        const isOneTime = this.getActivePaymentOption() === 'one-time';
        const isAvailable = currentVariantData.available === 'true';
        this.buyOnceForm.classList.toggle('hidden', isOneTime || !isAvailable);
      }
    }

    updateDeliveryInterval(currentVariantData) {
      if (this.currentDeliveryInterval && currentVariantData.deliveryInterval) {
        this.currentDeliveryInterval.textContent = currentVariantData.deliveryInterval;
      }
    }

    updateButtonVisibility(currentVariantData) {
      if (this.addToCartButton) {
        this.addToCartButton && this.addToCartButton.classList.toggle('hidden', !currentVariantData.available);
      }

      if (this.notifyMeButton) {
        this.notifyMeButton && this.notifyMeButton.classList.toggle('hidden', currentVariantData.available);
      }
    }

    updatePriceDisplay() {
      const activePaymentOption = this.getActivePaymentOption();
      const activeVariantInput = this.getActiveVariantInput();

      if (activePaymentOption === 'one-time') {
        this.updateOneTimePrices(activeVariantInput);
        this.pauseOrCancelAnytimeText && this.pauseOrCancelAnytimeText.classList.add('hidden');
      } else if (activePaymentOption === 'subscription') {
        this.updateSubscriptionPrices(activeVariantInput);
        this.pauseOrCancelAnytimeText && this.pauseOrCancelAnytimeText.classList.remove('hidden');
      }
    }

    updateOneTimePrices(activeVariantInput) {
      const { variantPrice, variantCompareAtPrice } = activeVariantInput.dataset;

      this.setMainPrice(variantPrice);
      this.setCompareAtPrice(variantCompareAtPrice, variantPrice);
    }

    updateSubscriptionPrices(activeVariantInput) {
      const { variantSubscriptionPrice, variantCompareAtPrice, variantPrice } = activeVariantInput.dataset;

      this.setMainPrice(variantSubscriptionPrice);
      this.setCompareAtPrice(variantCompareAtPrice, variantSubscriptionPrice);

      if (this.buyOncePrice && variantPrice) {
        this.buyOncePrice.textContent = variantPrice;
      }
    }

    setMainPrice(price) {
      if (this.addToCartPrice) {
        this.addToCartPrice.textContent = price;
      }
    }

    setCompareAtPrice(compareAtPrice, mainPrice) {
      if (!this.addToCartCompareAtPrice) return;

      // Hide compare at price if it's the same as the main price or doesn't exist
      if (compareAtPrice && compareAtPrice !== mainPrice) {
        this.addToCartCompareAtPrice.textContent = compareAtPrice;
        this.addToCartCompareAtPrice.classList.remove('hidden');
      } else {
        this.addToCartCompareAtPrice.classList.add('hidden');
      }
    }

    handleSwatchClick(eventTarget) {
      const swatch = eventTarget.closest?.('.custom-variant-picker-toggle__swatch-item');
      if (!swatch) return;

      if (swatch.id === 'swatch-item-one-time-purchase') {
        this.activateOneTimeSwatch();
      } else if (swatch.id === 'swatch-item-subscribe-and-save') {
        this.activateSubscriptionSwatch();
      }
    }

    activateOneTimeSwatch() {
      this.updateSwatchState('one-time');
      this.updateVariantOptions('one-time');
      this.updatePriceDisplay();
    }

    activateSubscriptionSwatch() {
      this.updateSwatchState('subscription');
      this.updateVariantOptions('subscription');
      this.updatePriceDisplay();
    }

    updateSwatchState(paymentType) {
      if (paymentType === 'one-time') {
        this.oneTimeSwatch && this.oneTimeSwatch.classList.add('active');
        this.subscribeSwatch && this.subscribeSwatch.classList.remove('active');
        this.formContainer && this.formContainer.classList.add('one-time-selected');
      } else {
        this.oneTimeSwatch && this.oneTimeSwatch.classList.remove('active');
        this.subscribeSwatch && this.subscribeSwatch.classList.add('active');
        this.formContainer && this.formContainer.classList.remove('one-time-selected');
      }
    }

    updateVariantOptions(paymentType) {
      const variantInputs = this.form.querySelectorAll('input.custom-variant-input');

      Array.from(variantInputs).forEach((variantInput, index) => {
        if (paymentType === 'one-time') {
          this.updateOneTimeVariantOption(variantInput, index);
        } else {
          this.updateSubscriptionVariantOption(variantInput, index);
        }
      });
    }

    updateOneTimeVariantOption(variantInput, index) {
      const { variantUnitPrice, variantSavings } = variantInput.dataset;

      this.setOptionUnitPrice(index, variantUnitPrice);
      this.setOptionSavings(index, this.hasMeaningfulSavings(variantSavings) ? variantSavings : '');
    }

    updateSubscriptionVariantOption(variantInput, index) {
      const { variantSubscriptionUnitPrice, variantSubscriptionSavings } = variantInput.dataset;

      this.setOptionUnitPrice(index, variantSubscriptionUnitPrice);
      this.setOptionSavings(
        index,
        this.hasMeaningfulSavings(variantSubscriptionSavings) ? variantSubscriptionSavings : '',
      );
    }

    hasMeaningfulSavings(value) {
      if (value == null || value === '') return false;
      const amount = Number.parseFloat(String(value).replace('%', '').trim());
      return !Number.isNaN(amount) && amount > 0;
    }

    setOptionUnitPrice(index, price) {
      if (this.optionUnitPrices[index]) {
        this.optionUnitPrices[index].textContent = price;
      }
    }

    setOptionSavings(index, savings) {
      if (this.hasMeaningfulSavings(savings)) {
        this.savingsWrapper[index] && this.savingsWrapper[index].classList.remove('hidden');
        this.optionUnitPriceWrapper[index] && this.optionUnitPriceWrapper[index].classList.remove('hidden');
      } else {
        this.savingsWrapper[index] && this.savingsWrapper[index].classList.add('hidden');
        this.optionUnitPriceWrapper[index] && this.optionUnitPriceWrapper[index].classList.add('hidden');
        return;
      }

      if (this.savings[index]) {
        this.savings[index].textContent = savings;
      }
    }

    getActiveVariantInput() {
      return this.form.querySelector('input[type="radio"]:checked');
    }

    handleUpdateUrl(currentVariantData) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', currentVariantData.variantId);
      window.history.replaceState({}, '', url);
    }

    // Cart and API Methods
    async addToCart(item) {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ items: [item] }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message);
      }

      const result = await response.json();

      return result;
    }

    dispatchCartRefresh() {
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      document.dispatchEvent(new CustomEvent('variant:add'));
    }

    closeModalIfNeeded() {
      if (this.form.dataset.closeModalOnAdd === 'true') {
        const quickBuyDrawer = this.closest('quick-buy-drawer');
        if (quickBuyDrawer && typeof quickBuyDrawer.hide === 'function') {
          quickBuyDrawer.hide();
        }
      }
    }

    // Utility Methods
    getCurrentVariantData() {
      const currentVariant = this.form.querySelector('input[type="radio"]:checked');
      const { available, variantId, sellingPlanId, variantPrice, variantCompareAtPrice, variantSubscriptionPrice, deliveryInterval } =
        currentVariant.dataset;

      return {
        variantId,
        sellingPlanId,
        variantPrice,
        variantCompareAtPrice,
        variantSubscriptionPrice,
        deliveryInterval,
        available,
      };
    }

    getActivePaymentOption() {
      if (this.oneTimeSwatch.classList.contains('active')) {
        return 'one-time';
      }

      if (this.subscribeSwatch.classList.contains('active')) {
        return 'subscription';
      }
    }
  }
);
